import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Row, Col, Input, Label } from "reactstrap";
import { CiGift, CiCreditCard1 } from "react-icons/ci";
import { BsInfoCircle } from "react-icons/bs";
import "./refundAllocation.scss";
import ButtonLoader from "../../../../share-components/ui/buttonLoader";
import { formatter } from "../../../../utils/common";

const REFUND_METHODS = {
  ORIGINAL: "original",
  CUSTOM: "custom",
  GIFT_CARD: "gift_card",
};

const toMoneyNumber = (value) => {
  const number = parseFloat(value);
  return Number.isNaN(number) ? 0 : Math.round(number * 100) / 100;
};

const toMoneyString = (value) => toMoneyNumber(value).toFixed(2);

const gatewayKey = (gateway, index) => `${gateway.gateway}-${gateway.parent_id || index}`;

const isGiftCardGateway = (gateway) => gateway.gateway === "gift_card";

const getGatewayLabel = (gateway) => {
  if (isGiftCardGateway(gateway)) return "Gift Card Refund";
  if (gateway.gateway === "shopify_payments") return "Shopify Payments Refund";
  if (gateway.gateway === "manual") return "Card Refund";

  return `${gateway.gateway.replace(/_/g, " ")} Refund`;
};

const buildGatewayAllocation = (gateways, amountMap) =>
  gateways.map((gateway, index) => ({
    gateway: gateway.gateway,
    parent_id: gateway.parent_id,
    amount: toMoneyNumber(amountMap[gatewayKey(gateway, index)]),
    maximum_refundable: toMoneyNumber(gateway.maximum_refundable),
  }));

function MethodCard({ label, description, active, onClick }) {
  return (
    <div className={`ra-method-card${active ? " active" : ""}`} onClick={onClick} role="radio" aria-checked={active} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onClick()}>
      <div className="ra-method-radio">
        <div className="ra-radio-dot">{active && <div className="ra-radio-dot-inner" />}</div>
        <div>
          <div className="ra-method-title">{label}</div>
          <div className="ra-method-desc">{description}</div>
        </div>
      </div>
    </div>
  );
}

function PaymentInputRow({ icon, iconClass, name, maxAmount, value, onChange, onBlur, error }) {
  return (
    <>
      <Row className="ra-payment-row g-0" style={error ? { marginBottom: 0 } : { marginBottom: 14 }}>
        <Col md={2}>
          <div className={`ra-payment-icon ${iconClass}`}>{icon}</div>
        </Col>

        <Col md={6} className="ra-payment-info">
          <div className="ra-payment-name">{name}</div>
          <div className="ra-payment-max">Maximum refundable: {formatter.format(maxAmount)}</div>
        </Col>

        <Col md={4}>
          <div className="ra-amount-input-wrap">
            <div className="ra-input-inner" style={{ borderColor: error ? "red" : "" }}>
              <span className="ra-currency">&pound;</span>
              <input type="text" value={value} onChange={onChange} onBlur={onBlur} />
            </div>
          </div>
        </Col>
      </Row>
      <div className={error ? "ra-input-error" : "ra-input-hint"}>{error || `Max ${formatter.format(maxAmount)}`}</div>
    </>
  );
}

function AmountInput({ value, onChange, onBlur, error, hint }) {
  return (
    <div className="ra-amount-input-wrap" style={{ alignItems: "flex-start" }}>
      <div className="ra-input-inner" style={{ borderColor: error ? "red" : "" }}>
        <span className="ra-currency">&pound;</span>
        <input type="text" value={value} onChange={onChange} onBlur={onBlur} aria-label="amount" />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, width: "100%", overflow: "hidden", marginBottom: 14, marginTop: 5 }}>
        <div className="ra-input-hint">{hint || ""}</div>
        <div style={{ color: "#ef4444" }}>{error || ""}</div>
      </div>
    </div>
  );
}

function CreateGiftCardPanel({ total, gateways, onChange }) {
  const [giftCardAmount, setGiftCardAmount] = useState(toMoneyString(total));
  const [refundRemaining, setRefundRemaining] = useState(false);
  const [refundGatewayKey, setRefundGatewayKey] = useState("");
  const [refundAmount, setRefundAmount] = useState("0.00");

  const giftCardVal = toMoneyNumber(giftCardAmount);
  const refundVal = refundRemaining ? toMoneyNumber(refundAmount) : 0;
  const leftover = Math.max(toMoneyNumber(total - giftCardVal), 0);
  const selectedGateway = gateways.find((gateway, index) => gatewayKey(gateway, index) === refundGatewayKey);
  const selectedGatewayMax = selectedGateway ? toMoneyNumber(selectedGateway.maximum_refundable) : 0;

  const giftCardError = giftCardVal <= 0 ? "Please enter a gift card value" : giftCardVal > total ? `Cannot exceed ${formatter.format(total)}` : "";
  const refundError = refundRemaining && refundVal > Math.min(leftover, selectedGatewayMax) ? `Cannot exceed ${formatter.format(Math.min(leftover, selectedGatewayMax))}` : "";
  const isValid = !giftCardError && !refundError && giftCardVal <= total && (!refundRemaining || (refundGatewayKey && refundVal > 0 && refundVal <= leftover));

  useEffect(() => {
    setGiftCardAmount(toMoneyString(total));
    setRefundRemaining(false);
    setRefundGatewayKey("");
    setRefundAmount("0.00");
  }, [total, gateways]);

  useEffect(() => {
    const gatewayAllocations = gateways.map((gateway, index) => ({
      gateway: gateway.gateway,
      parent_id: gateway.parent_id,
      amount: refundRemaining && gatewayKey(gateway, index) === refundGatewayKey ? refundVal : 0,
      maximum_refundable: toMoneyNumber(gateway.maximum_refundable),
    }));

    onChange({
      method: REFUND_METHODS.GIFT_CARD,
      isValid,
      create_gift_card_amount: giftCardVal,
      gateway_allocations: gatewayAllocations,
      totalAllocated: toMoneyNumber(giftCardVal + refundVal),
      remaining: toMoneyNumber(total - giftCardVal - refundVal),
      errors: [giftCardError, refundError].filter(Boolean),
    });
  }, [giftCardVal, refundVal, refundRemaining, refundGatewayKey, isValid, total, gateways, giftCardError, refundError, onChange]);

  const handleAmountChange = (setter) => (e) => {
    if (/^\d*\.?\d{0,2}$/.test(e.target.value)) setter(e.target.value);
  };

  return (
    <div className="ra-gc-panel">
      <div>
        <div className="ra-gc-field-label">
          <CiGift size={16} color="#7c3aed" style={{ marginRight: 6, verticalAlign: "middle" }} />
          New Gift Card Value
        </div>
        <AmountInput
          value={giftCardAmount}
          onChange={handleAmountChange(setGiftCardAmount)}
          onBlur={() => setGiftCardAmount(toMoneyString(giftCardAmount))}
          error={giftCardError}
          hint={`Max ${formatter.format(total)}`}
        />
      </div>

      {leftover > 0 && (
        <div className="ra-gc-remaining-box">
          <div className="ra-gc-remaining-question">
            Refund the remaining <span className="amount">{formatter.format(leftover)}</span> to the customer?
          </div>
          <div className="ra-gc-yn-btns">
            <button className={`ra-gc-yn-btn${refundRemaining ? " active" : ""}`} type="button" onClick={() => setRefundRemaining(true)}>
              Yes
            </button>
            <button
              className={`ra-gc-yn-btn${!refundRemaining ? " active" : ""}`}
              type="button"
              onClick={() => {
                setRefundRemaining(false);
                setRefundGatewayKey("");
                setRefundAmount("0.00");
              }}
            >
              No
            </button>
          </div>

          {refundRemaining && (
            <>
              <div className="ra-gc-refund-methods">
                {gateways.map((gateway, index) => {
                  const key = gatewayKey(gateway, index);
                  return (
                    <div key={key} className={`ra-gc-refund-method-card${refundGatewayKey === key ? " active" : ""}`} onClick={() => setRefundGatewayKey(key)}>
                      <div className="ra-radio-dot">{refundGatewayKey === key && <div className="ra-radio-dot-inner" />}</div>
                      {isGiftCardGateway(gateway) ? <CiGift size={18} color="#7c3aed" /> : <CiCreditCard1 size={18} color="#3b82f6" />}
                      <span className="ra-gc-refund-method-label">{isGiftCardGateway(gateway) ? "Gift Card" : "Card"}</span>
                    </div>
                  );
                })}
              </div>

              {refundGatewayKey && (
                <div style={{ marginTop: 12 }}>
                  <div className="ra-gc-field-label">Refund Amount</div>
                  <AmountInput
                    value={refundAmount}
                    onChange={handleAmountChange(setRefundAmount)}
                    onBlur={() => setRefundAmount(toMoneyString(refundAmount))}
                    error={refundError}
                    hint={`Max ${formatter.format(Math.min(leftover, selectedGatewayMax))}`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Add this helper function near the top (after toMoneyString)
const distributeShippingToGateways = (gateways, shippingAmount) => {
  if (!shippingAmount || shippingAmount <= 0) return gateways;

  // Find non-gift-card gateway first (card), fallback to gift card
  const cardIndex = gateways.findIndex((g) => !isGiftCardGateway(g));
  const targetIndex = cardIndex >= 0 ? cardIndex : 0;

  return gateways.map((gateway, index) => {
    if (index !== targetIndex) return gateway;

    const newAmount = toMoneyNumber(gateway.amount) + toMoneyNumber(shippingAmount);
    return {
      ...gateway,
      amount: newAmount,
      // Don't change maximum_refundable — that's the hard limit
    };
  });
};

function RefundAllocation(props) {
  const {
    totalRefund = 0,
    giftCardMax = 0,
    cardMax = 0,
    isMixedPayment = false,
    isLoading = false,
    gateways = [],
    onAllocationChange,
    setValue,
    getValues,
    buttonDisabled,
    submitData,
    suggestedTotalRefund, // now item-only from Shopify
    shippingAmount = 0,
    isShippingOnly = false,
    hasRefundItems,
  } = props;

  const total = toMoneyNumber(totalRefund);
  const apiGateways = useMemo(() => gateways.filter((g) => toMoneyNumber(g.maximum_refundable) > 0 || toMoneyNumber(g.amount) > 0), [gateways]);
  const gatewaysWithShipping = useMemo(() => distributeShippingToGateways(apiGateways, shippingAmount), [apiGateways, shippingAmount]);
  const fallbackGateways = useMemo(
    () =>
      gatewaysWithShipping.length > 0
        ? gatewaysWithShipping
        : [
            { gateway: "gift_card", parent_id: null, amount: 0, maximum_refundable: giftCardMax },
            { gateway: "card", parent_id: null, amount: total, maximum_refundable: cardMax },
          ].filter((g) => toMoneyNumber(g.maximum_refundable) > 0),
    [gatewaysWithShipping, giftCardMax, cardMax, total],
  );
  const originalAmounts = useMemo(() => {
    const amounts = {};
    fallbackGateways.forEach((gateway, index) => {
      amounts[gatewayKey(gateway, index)] = toMoneyString(gateway.amount);
    });
    return amounts;
  }, [fallbackGateways]);


  const [method, setMethod] = useState(REFUND_METHODS.ORIGINAL);
  const [customAmounts, setCustomAmounts] = useState(() => originalAmounts);
  const [giftCardAllocation, setGiftCardAllocation] = useState(null);

  const amountMap = method === REFUND_METHODS.ORIGINAL ? originalAmounts : customAmounts;
  const totalAllocated = fallbackGateways.reduce((sum, gateway, index) => sum + toMoneyNumber(amountMap[gatewayKey(gateway, index)]), 0);
  const remaining = toMoneyNumber(total - totalAllocated);

  useEffect(() => {
    if (method !== REFUND_METHODS.CUSTOM) {
      setCustomAmounts(originalAmounts);
    }
  }, [method, originalAmounts]);

  const errors = useMemo(() => {
    const newErrors = {};

    if (method === REFUND_METHODS.CUSTOM) {
      fallbackGateways.forEach((gateway, index) => {
        const key = gatewayKey(gateway, index);
        const value = toMoneyNumber(customAmounts[key]);
        const max = toMoneyNumber(gateway.maximum_refundable);

        if (value < 0) newErrors[key] = `Amount cannot be negative`;
        else if (value > max) newErrors[key] = `Cannot exceed ${formatter.format(max)}`;
      });

      // Only error if over-allocated — under-allocation is fine
      if (remaining < -0.01) {
        newErrors.total = `Over-allocated by ${formatter.format(Math.abs(remaining))} — cannot exceed ${formatter.format(total)}`;
      }
    }

    return newErrors;
  }, [method, customAmounts, fallbackGateways, remaining, total]);

  const isAllocationValid = method === REFUND_METHODS.GIFT_CARD ? giftCardAllocation?.isValid === true : Object.keys(errors).length === 0;

  useEffect(() => {
    setMethod(REFUND_METHODS.ORIGINAL);
    setCustomAmounts(originalAmounts);
    setGiftCardAllocation(null);
  }, [originalAmounts]);

  useEffect(() => {
    if (!onAllocationChange) return;

    if (method === REFUND_METHODS.GIFT_CARD) {
      onAllocationChange(giftCardAllocation);
      return;
    }

    onAllocationChange({
      method,
      isValid: isAllocationValid,
      gateway_allocations: buildGatewayAllocation(fallbackGateways, amountMap),
      totalAllocated: toMoneyNumber(totalAllocated),
      remaining,
      errors: Object.values(errors),
    });
  }, [method, giftCardAllocation, isAllocationValid, fallbackGateways, amountMap, totalAllocated, remaining, errors, onAllocationChange]);

  useEffect(() => {
    if (isShippingOnly && method === REFUND_METHODS.GIFT_CARD) {
      setMethod(REFUND_METHODS.ORIGINAL);
    }
  }, [isShippingOnly]);

  const handleChange = (key) => (e) => {
    if (/^\d*\.?\d{0,2}$/.test(e.target.value)) {
      setCustomAmounts((current) => ({ ...current, [key]: e.target.value }));
    }
  };

  const handleBlur = (key) => () => {
    setCustomAmounts((current) => ({ ...current, [key]: toMoneyString(current[key]) }));
  };

  const remainingClass = remaining === 0 ? "green" : remaining < 0 ? "red" : "";

  if (isLoading) {
    return (
      <Card className="border shadow-sm">
        <CardBody className="ra-root">
          <h5>Refund Allocation</h5>
          <div className="text-center p-4">
            <div className="spinner-border spinner-border-sm text-primary" />
            <p className="mt-2 text-muted">Calculating payment limits...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="border shadow-sm">
      <CardBody className="ra-root">
        <h5>Refund Allocation</h5>
        <p className="ra-subtitle">Select how you want to refund {formatter.format(total)}</p>

        <div className="ra-section-label">Refund Method</div>
        <div className="ra-methods">
          <MethodCard label="Original Payment Split" description="Refund as per original payment" active={method === REFUND_METHODS.ORIGINAL} onClick={() => setMethod(REFUND_METHODS.ORIGINAL)} />
          {hasRefundItems && isMixedPayment && (
            <MethodCard
              label="Custom Split"
              description="Manually split between payment methods"
              active={method === REFUND_METHODS.CUSTOM}
              onClick={() => {
                if (Object.keys(customAmounts).length === 0) {
                  setCustomAmounts(originalAmounts);
                }
                setMethod(REFUND_METHODS.CUSTOM);
              }}
            />
          )}
          {hasRefundItems && !isShippingOnly && (
            <MethodCard
              label="Create Gift Card"
              description="Create new gift card and/or refund remaining"
              active={method === REFUND_METHODS.GIFT_CARD}
              onClick={() => setMethod(REFUND_METHODS.GIFT_CARD)}
            />
          )}
        </div>

        {method === REFUND_METHODS.CUSTOM && (
          <div className="ra-allocation-panel">
            <p className="ra-allocation-label">Allocate refund amount to each payment method</p>

            {fallbackGateways.map((gateway, index) => {
              const key = gatewayKey(gateway, index);
              return (
                <PaymentInputRow
                  key={key}
                  icon={isGiftCardGateway(gateway) ? <CiGift size={24} color="#7c3aed" /> : <CiCreditCard1 size={24} color="#3b82f6" />}
                  iconClass={isGiftCardGateway(gateway) ? "gift" : "card"}
                  name={getGatewayLabel(gateway)}
                  maxAmount={toMoneyNumber(gateway.maximum_refundable)}
                  value={customAmounts[key] ?? "0.00"}
                  onChange={handleChange(key)}
                  onBlur={handleBlur(key)}
                  error={errors[key]}
                />
              );
            })}

            <hr className="my-3" />
            <div className="ra-totals">
              <div className="ra-total-row">
                <span className="label">Total Allocated</span>
                <span className="amount">{formatter.format(totalAllocated)}</span>
              </div>
              <div className="ra-total-row">
                <span className="label">Remaining</span>
                <span className={`amount ${remaining < 0 ? "red" : ""}`}>{formatter.format(remaining)}</span>
              </div>
            </div>

            {errors.total && <div className="ra-input-error mt-1">{errors.total}</div>}
          </div>
        )}

        {method === REFUND_METHODS.GIFT_CARD && <CreateGiftCardPanel total={total} gateways={fallbackGateways} onChange={setGiftCardAllocation} />}

        <div className="ra-summary">
          <div className="ra-summary-title">Refund Summary</div>

          <Row className="ra-summary-row">
            <Col>Total Refund Amount</Col>
            <Col xs="auto">{formatter.format(total)}</Col>
          </Row>

          {method !== REFUND_METHODS.GIFT_CARD &&
            fallbackGateways.map((gateway, index) => {
              const key = gatewayKey(gateway, index);
              const amount = method === REFUND_METHODS.ORIGINAL ? gateway.amount : amountMap[key];

              return (
                <Row className="ra-summary-row" key={key}>
                  <Col>{getGatewayLabel(gateway)}</Col>
                  <Col xs="auto">{formatter.format(toMoneyNumber(amount))}</Col>
                </Row>
              );
            })}

          {method === REFUND_METHODS.GIFT_CARD && (
            <>
              <Row className="ra-summary-row">
                <Col>New Gift Card</Col>
                <Col xs="auto">{formatter.format(giftCardAllocation?.create_gift_card_amount || 0)}</Col>
              </Row>
              {(giftCardAllocation?.gateway_allocations || [])
                .filter((allocation) => allocation.amount > 0)
                .map((allocation) => (
                  <Row className="ra-summary-row" key={`${allocation.gateway}-${allocation.parent_id}`}>
                    <Col>{getGatewayLabel(allocation)}</Col>
                    <Col xs="auto">{formatter.format(allocation.amount)}</Col>
                  </Row>
                ))}
            </>
          )}

          {/* {shippingAmount && shippingAmount > 0 && (
            <Row className="ra-summary-row">
              <Col>Shipping</Col>
              <Col xs="auto">{formatter.format(shippingAmount)}</Col>
            </Row>
          )} */}

          <hr className="my-2" />

          <div className="ra-summary-total">
            <span>Total</span>
            <span>
              {formatter.format(
                method === REFUND_METHODS.GIFT_CARD ? giftCardAllocation?.totalAllocated || 0 : total, // ← this already includes shipping via originalAmounts
              )}
            </span>
          </div>
        </div>

        {method === REFUND_METHODS.CUSTOM && (
          <div className="ra-warning">
            <BsInfoCircle color="#d97706" size={24} />
            <span>You cannot refund more than the original amount paid with each payment method.</span>
          </div>
        )}

        <Row className="mt-4">
          <Col>
            {(() => {
              const itemOnlyTotal = toMoneyNumber(totalRefund) - toMoneyNumber(shippingAmount);
              const suggested = toMoneyNumber(suggestedTotalRefund);
              return suggested > 0 && Math.abs(suggested - itemOnlyTotal) > 0.01;
            })() && (
              <div className="alert alert-warning" role="alert">
                <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                The item refund amount differs from the Shopify suggested amount.
              </div>
            )}

            <div className="checkbox checkbox-dark px-4">
              <Input
                id="is_notify"
                type="checkbox"
                onChange={(e) => {
                  setValue("is_notify", e.target.checked);
                }}
                defaultChecked={getValues("is_notify")}
              />
              <Label for="is_notify">Send a notification to the customer</Label>
            </div>

            <hr />

            <ButtonLoader
              disabled={buttonDisabled || !isAllocationValid}
              onClick={submitData}
              loading={buttonDisabled}
              btntext={
                "Refund " +
                formatter.format(
                  method === REFUND_METHODS.GIFT_CARD ? giftCardAllocation?.totalAllocated || 0 : total, // ← this already includes shipping via originalAmounts
                )
              }
              className="btn btn-primary btn-block"
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}

export default RefundAllocation;
